import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { ApplicantDiscussion } from '@app/models/recruiting/applicant-discussion.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { DiscussionComment } from '@app/models/recruiting/discussion-comment.model';
import { AnalyticService, NotifyService } from '@app/services';
import { HiringAccessLevelIdsEnum } from '@applicant-tracker/enums/hiring-access-levels.enum';
import { Subscription } from 'rxjs';
import { ApplicantProfileService } from '../../services';

@Component({
    selector: 'app-comments-mini',
    templateUrl: './comments-mini.template.html',
    styleUrls: ['./comments-mini.style.scss'],
})
export class CommentsMiniComponent implements OnInit, OnDestroy {
    @ViewChild('confirmDeleteComment') confirmDeleteComment!: DialogComponent;

    @Input() miniState = false;
    applicant: Applicant | null = null;
    hiringTeam: Employee[] = [];
    applicantDiscussions: ApplicantDiscussion[] = [];
    commentToDelete: DiscussionComment | null = null;
    applicantProfileSubscription: Subscription;
    hiringAccessLevelIdsEnum = HiringAccessLevelIdsEnum;

    isLoading = true;
    isDeleteCommentDialogOpen = false;

    constructor(
        private analyticService: AnalyticService,
        private applicantProfileService: ApplicantProfileService,
        private notify: NotifyService,
        private router: Router
    ) {
        this.applicantProfileSubscription = this.applicantProfileService.applicantSubject.subscribe(
            (applicant: Applicant) => {
                this.applicant = applicant;
            }
        );
    }

    async ngOnInit(): Promise<void> {
        if (!this.applicant) {
            throw new Error('Applicant is not defined');
        }

        await this.fetchAndAssignDiscussions();

        this.hiringTeam = this.applicant.jobPosting.managerAssignments.map(
            (managerAssignment) => managerAssignment.employee
        );

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.applicantProfileSubscription.unsubscribe();
    }

    viewAllComments(): void {
        this.router.navigate(['/recruiting', 'applicants', this.applicant?.id, 'comments']);
    }

    closeDeleteCommentDialog(): void {
        this.commentToDelete = null;
        this.isDeleteCommentDialogOpen = false;
    }

    openDeleteCommentDialog(comment: DiscussionComment): void {
        this.commentToDelete = comment;
        this.isDeleteCommentDialogOpen = true;
    }

    async onDeleteComment(): Promise<void> {
        if (!this.commentToDelete) {
            return;
        }

        this.isDeleteCommentDialogOpen = false;
        this.isLoading = true;

        try {
            await this.commentToDelete.delete();
            this.applicantProfileService.clearApplicantDiscussion();
            await this.fetchAndAssignDiscussions();
            this.notify.success('applicant-tracker.commentHasBeenSuccessfullyDeleted');
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;
    }

    async onSaveComment(comment: DiscussionComment, applicantDiscussion: ApplicantDiscussion): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantDiscussionComment);
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantProfileMiniPanelTabComment);
        this.isLoading = true;

        try {
            const notificationVerb = comment.id ? 'updated' : 'posted';
            comment.atsApplicantDiscussionId = applicantDiscussion.id;

            comment.isPrivate = comment.accessLevelId === HiringAccessLevelIdsEnum.private;

            await comment.save();
            this.applicantProfileService.clearApplicantDiscussion();
            await this.fetchAndAssignDiscussions();
            this.notify.success({
                key: 'applicant-tracker.yourCommentHasBeenActionedSuccessfully',
                params: { action: notificationVerb },
            });
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;
    }

    private async fetchAndAssignDiscussions(): Promise<void> {
        if (!this.applicant) {
            throw new Error('Applicant is not defined');
        }

        try {
            this.applicantDiscussions = await this.applicantProfileService.getDiscussions(this.applicant);
        } catch (e) {
            this.notify.error(e);
        }
    }
}
